import { motion } from "framer-motion";
import { Formik, Form, Field, ErrorMessage } from "formik";
import {
  TextField,
} from "@mui/material";
import * as Yup from "yup";
import Images from "../../Images";
import { useMutation } from "react-query";
import { useDispatch } from "react-redux";
import { setUser } from "../../redux/UserSlice";
import { loginUser } from "../../requests/auth/Authentication";
import { toast } from "react-toastify";
import { useNavigate, Link } from "react-router-dom";

// * Request Validation
const validationSchema = Yup.object({
  email: Yup.string()
    .required("Email is required")
    .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, "Invalid Email Entered"),
  password: Yup.string().required("Password is required"),
});

const initialValues = {
  email: "",
  password: "",
};

function SigninEmail() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Todo => User Registration method
  const loginUserMutation = useMutation(loginUser, {
    onSuccess: (data) => {
      dispatch(setUser(data));
      toast.success("Login successful!");
      navigate("/dashboard");
    },
    onError: (error) => {
      toast.error(error?.message || "Sorry, Something went wrong");
    },
  });

  // Add loading state from mutation
  const isLoading = loginUserMutation.isLoading;

  return (
    <div className="h-screen w-full flex flex-col sm:flex-row bg-[#FEFEFE]">
      {/* Left Side */}
      <motion.div
        className="relative hidden w-full h-full sm:w-1/2 sm:flex items-center justify-center overflow-hidden bg-gradient-to-br from-[#FF6C6C] via-[#FF8B8B] to-[#FF9B9B]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        {/* Background animations */}
        <motion.div
          className="absolute w-[800px] h-[800px] bg-white/10 rounded-full blur-xl"
          initial={{ scale: 0, rotate: 0 }}
          animate={{ 
            scale: [1, 1.2, 1],
            rotate: [0, 180, 360],
          }}
          transition={{ 
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
        />
        
        {/* Floating stats cards */}
        <motion.div
          className="absolute top-10 right-10 bg-white/30 backdrop-blur-md rounded-2xl p-4 shadow-lg z-20"
          initial={{ x: 100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 0.8, duration: 0.8 }}
          whileHover={{ scale: 1.05 }}
        >
          <div className="flex items-center gap-3">
            <div className="w-3 h-3 rounded-full bg-green-400 animate-pulse"></div>
            <span className="text-white text-sm font-medium">1M+ Email Users</span>
          </div>
        </motion.div>

        <motion.div
          className="absolute bottom-20 left-10 bg-white/30 backdrop-blur-md rounded-2xl p-4 shadow-lg z-20"
          initial={{ x: -100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 1, duration: 0.8 }}
          whileHover={{ scale: 1.05 }}
        >
          <div className="flex items-center gap-3">
            <div className="w-3 h-3 rounded-full bg-yellow-400 animate-pulse"></div>
            <span className="text-white text-sm font-medium">Secure Email Login</span>
          </div>
        </motion.div>

        {/* Main image container */}
        <motion.div
          className="relative z-10 w-4/5 h-4/5"
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 1, delay: 0.5 }}
        >
          <motion.div
            className="relative w-full h-full rounded-2xl overflow-hidden shadow-2xl bg-white/10 backdrop-blur-sm"
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.3 }}
          >
            <motion.img
              src={Images.register_seven}
              alt="dealboxx-email-login"
              className="object-cover w-full h-full rounded-2xl hover:scale-110 transition-transform duration-700"
              initial={{ scale: 1.1 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.6 }}
            />
            
            {/* Content overlay */}
            <motion.div 
              className="absolute inset-0 bg-gradient-to-b from-black/10 via-black/20 to-black/60 flex flex-col justify-end items-center pb-20"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1 }}
            >
              <motion.div
                className="text-center px-8"
                initial={{ y: 20 }}
                animate={{ y: 0 }}
              >
                <motion.h2 
                  className="text-5xl font-bold mb-6 text-white drop-shadow-lg"
                  initial={{ y: 20 }}
                  animate={{ y: 0 }}
                  transition={{ delay: 1.2 }}
                >
                  Email Login
                </motion.h2>
                <motion.p 
                  className="text-xl text-white/90 max-w-md mx-auto mb-8"
                  initial={{ y: 20 }}
                  animate={{ y: 0 }}
                  transition={{ delay: 1.4 }}
                >
                  Access your account securely with email
                </motion.p>
              </motion.div>
            </motion.div>
          </motion.div>
        </motion.div>
      </motion.div>

      {/* Right Side */}
      <motion.div 
        className="w-full px-8 pt-16 pb-8 overflow-y-auto sm:w-1/2 sm:h-screen sm:px-16"
        initial={{ opacity: 0, x: 50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8 }}
      >
        {/* Logo */}
        <motion.div
          className="mb-6"
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.8 }}
        >
          <img src={Images.logo} alt="Dealboxx Logo" className="w-32 h-auto"/>
        </motion.div>

        {/* Back Button */}
        <motion.div 
          className="mb-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
        >
        </motion.div>

        {/* Welcome Text */}
        <motion.div
          className="space-y-2 mb-6"
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.8 }}
        >
          <h1 className="text-3xl font-bold bg-gradient-to-r from-[#FF6C6C] to-[#FF9B9B] bg-clip-text text-transparent">
            Welcome Back
          </h1>
          <p className="text-[#808080] text-base">
            Sign in with your email to continue
          </p>
        </motion.div>

        {/* Form */}
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={values => {
            loginUserMutation.mutate(values);
          }}
        >
          {({ handleSubmit, touched, errors }) => (
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.7, duration: 0.8 }}
            >
              <Form onSubmit={handleSubmit} className="space-y-4">
                {/* Email Field */}
                <Field
                  name="email"
                  as={TextField}
                  label="Email"
                  variant="outlined"
                  fullWidth
                  error={Boolean(errors.email && touched.email)}
                  helperText={
                    <ErrorMessage
                      name="email"
                      component="div"
                      className="text-red-500 italic text-sm"
                    />
                  }
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      backgroundColor: 'white',
                      transition: 'transform 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                      },
                      '&.Mui-focused': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 12px rgba(255,108,108,0.2)',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#FF6C6C',
                        },
                      }
                    },
                    '& .MuiInputLabel-root': {
                      '&.Mui-focused': {
                        color: '#FF6C6C',
                      },
                    },
                  }}
                />

                {/* Password Field */}
                <Field
                  name="password"
                  as={TextField}
                  type="password"
                  label="Password"
                  variant="outlined"
                  fullWidth
                  error={Boolean(errors.password && touched.password)}
                  helperText={
                    <ErrorMessage
                      name="password"
                      component="div"
                      className="text-red-500 italic text-sm"
                    />
                  }
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      backgroundColor: 'white',
                      transition: 'transform 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                      },
                      '&.Mui-focused': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 12px rgba(255,108,108,0.2)',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#FF6C6C',
                        },
                      }
                    },
                    '& .MuiInputLabel-root': {
                      '&.Mui-focused': {
                        color: '#FF6C6C',
                      },
                    },
                  }}
                />

                {/* Forgot Password Link */}
                <motion.div 
                  className="flex justify-end"
                  whileHover={{ scale: 1.02 }}
                >
                  <Link
                    to="/forgot-password"
                    className="text-[#FF6C6C] font-semibold hover:text-[#ff8b8b] transition-colors duration-300"
                  >
                    Forgot Password?
                  </Link>
                </motion.div>

                {/* Updated Submit Button with Loading State */}
                <motion.button
                  type="submit"
                  disabled={isLoading}
                  className={`w-full bg-gradient-to-r from-[#FF6C6C] to-[#FF9B9B] text-white font-semibold py-3 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 relative ${
                    isLoading ? 'cursor-not-allowed opacity-80' : ''
                  }`}
                  whileHover={{ scale: isLoading ? 1 : 1.02 }}
                  whileTap={{ scale: isLoading ? 1 : 0.98 }}
                >
                  {isLoading ? (
                    <>
                      <span className="opacity-0">Login</span>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    </>
                  ) : (
                    'Login'
                  )}
                </motion.button>
              </Form>
            </motion.div>
          )}
        </Formik>
        {/* Feature Cards */}
        <motion.div
          className="mt-8 grid grid-cols-3 gap-3"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 1.3, duration: 0.8 }}
        >
          <div className="text-center p-3 rounded-xl bg-white shadow-sm hover:shadow-md transition-shadow duration-300">
            <motion.div whileHover={{ scale: 1.05 }}>
              <div className="text-[#FF6C6C] text-lg mb-1">📧</div>
              <div className="text-xs text-gray-600">Email Verified</div>
            </motion.div>
          </div>
          <div className="text-center p-3 rounded-xl bg-white shadow-sm hover:shadow-md transition-shadow duration-300">
            <motion.div whileHover={{ scale: 1.05 }}>
              <div className="text-[#FF6C6C] text-lg mb-1">🔒</div>
              <div className="text-xs text-gray-600">Secure Login</div>
            </motion.div>
          </div>
          <div className="text-center p-3 rounded-xl bg-white shadow-sm hover:shadow-md transition-shadow duration-300">
            <motion.div whileHover={{ scale: 1.05 }}>
              <div className="text-[#FF6C6C] text-lg mb-1">⚡</div>
              <div className="text-xs text-gray-600">Quick Access</div>
            </motion.div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
}

export default SigninEmail;
